import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchIngredients } from '../../store/ingredientsSlice';

const DEBOUNCE_MS = 300;

const IngredientsIndex: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, pagination, status, error } = useAppSelector((state) => state.ingredients);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    dispatch(fetchIngredients({ page, query: debouncedQuery }));
  }, [page, debouncedQuery, dispatch]);

  if (status === 'failed') {
    return <div className="text-rose-400 py-12 text-center">Error: {error}</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Food</h2>

      <div className="relative mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search food..."
          className="w-full px-4 py-2.5 pl-10 bg-army-900 border border-army-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors"
        />
        <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-army-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-army-700 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
        <span>
          {status === 'loading'
            ? 'Loading...'
            : `${pagination.totalCount} food${pagination.totalCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="bg-navy-900 border border-navy-800 rounded-xl overflow-hidden">
        <ul className="divide-y divide-navy-800">
          {items.map((item) => (
            <li
              key={item.id}
              className="px-5 py-3 text-gray-300 hover:text-white hover:bg-navy-800 transition-colors"
            >
              {item.name}
            </li>
          ))}
          {items.length === 0 && status === 'succeeded' && (
            <li className="px-5 py-8 text-center text-gray-500 text-sm">
              {searchQuery ? `No food matches "${debouncedQuery}"` : 'No food yet.'}
            </li>
          )}
        </ul>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || status === 'loading'}
            className="px-4 py-2 text-sm bg-army-900 border border-army-800 rounded-lg text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || status === 'loading'}
            className="px-4 py-2 text-sm bg-army-900 border border-army-800 rounded-lg text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default IngredientsIndex;
